import { useCallback, useMemo } from 'react';

import { QuantityUnit } from '@dreryk/drerykweb-medicine-api';

import { Bean, useTranslate, ValidationRules } from '@dreryk/react-common';
import { InteractiveTable, InteractiveTableCellProps, useInteractiveTableContext } from '@dreryk/react-components';

import { magistralDrugTranslationKeys, prescriptionsTranslationKeys } from '../../../../../../translations';

import { MedicineRawMaterialTableBean } from './MedicineRawMaterialTable.types';
import { StyledTooltip, StyledUnitSelectCell } from './UnitSelectCell.styled';

export type UnitSelectCellQuantityUnit = QuantityUnit | 'NONE';
type UnitSelectCellProps = InteractiveTableCellProps<UnitSelectCellQuantityUnit>;

export const useTriggerLabelExtractor = (): ((item?: UnitSelectCellQuantityUnit) => string) => {
   const translate = useTranslate();

   return useCallback(
      (item) => {
         return translate(prescriptionsTranslationKeys.quantityUnitShortcut[item ?? 'NONE']);
      },
      [translate]
   );
};

const QUANTITY_UNITS: UnitSelectCellQuantityUnit[] = [
   'NONE',
   'AMPOULES',
   'DROPS',
   'GRAMS',
   'MILLIGRAMS',
   'MICROGRAMS',
   'MILLILITRES',
   'PIECES',
   'MEASURE_UNITS',
];

const useValidationRules = (index: number, validationRules?: ValidationRules<Bean>): ValidationRules<Bean> =>
   useMemo(
      () => ({
         ...validationRules,
         validate: (data, getValue) => {
            const currentQuantity = getValue(`medicineRawMaterials[${index}].quantity`);
            if (currentQuantity && (data as UnitSelectCellQuantityUnit) === 'NONE') {
               return magistralDrugTranslationKeys.medicineRawMaterials.quantityRequiredIfUnitProvided;
            }
            return undefined;
         },
      }),
      [index, validationRules]
   );

export const UnitSelectCell = (props: UnitSelectCellProps): JSX.Element => {
   const { getCellValue } = useInteractiveTableContext<MedicineRawMaterialTableBean>();
   const triggerLabelExtractor = useTriggerLabelExtractor();

   const rpw = getCellValue('rpw', props.index);

   return (
      <StyledTooltip
         testId={props.testId}
         content={magistralDrugTranslationKeys.medicineRawMaterials.quantityAbbrWhenRpw}
         disabled={!rpw}
      >
         <StyledUnitSelectCell<UnitSelectCellQuantityUnit>
            testId={props.testId}
            name={props.name}
            index={props.index}
            validationRules={useValidationRules(props.index, props.validationRules)}
            defaultValue={props.defaultValue}
            triggerLabelExtractor={triggerLabelExtractor}
            disabled={rpw}
         >
            <InteractiveTable.SelectCell.List<UnitSelectCellQuantityUnit> items={QUANTITY_UNITS}>
               {(unit) => (
                  <InteractiveTable.SelectCell.Item
                     testId={unit}
                     item={unit}
                     label={prescriptionsTranslationKeys.quantityUnitShortcut[unit]}
                  />
               )}
            </InteractiveTable.SelectCell.List>
         </StyledUnitSelectCell>
      </StyledTooltip>
   );
};
